import { useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import WhiteButton from "../../components/WhiteButton";
import BlueButton from "../../components/BlueButton";
import Loading from "../../components/Loading";
import { TIPOS_ITEM, CLASSIFICACOES, UFS } from "./dominios";
import { parseCsvNcm } from "./parseCsvNcm.util";
import { CampoCnpj, useEmpresasDoGrupo } from "./CampoCnpj";
import { CamposAliquotaMetodo } from "./CamposAliquotaMetodo";
import {
  MOTIVO_SEM_PERMISSAO_GLOBAL,
  payloadAliquotaMetodo,
  sanitizarNcm,
} from "./aliquotaMetodo.util";
import {
  inserirItemTabelaNcm,
  importarLoteTabelaNcm,
  copiarBaseTabelaNcm,
} from "../../services/api-planejamento-tributario.service";

const MODO = {
  INDIVIDUAL: "individual",
  PLANILHA: "planilha",
  COPIA: "copia",
};

/** Títulos e subtítulos literais do protótipo (tabela_ncm.html, .modo-opcao). */
const MODOS = [
  { valor: MODO.INDIVIDUAL, titulo: "Inclusão individual", descricao: "Cadastrar um item por vez" },
  { valor: MODO.PLANILHA, titulo: "Upload de planilha", descricao: "Importar vários itens de um CSV" },
  { valor: MODO.COPIA, titulo: "Cópia de base", descricao: "Copiar o cadastro de outro cliente" },
];

const FORM_VAZIO = {
  estado: "",
  item: "",
  ncm: "",
  cest: "",
  descricao: "",
  tipo: TIPOS_ITEM[0],
  classificacao: CLASSIFICACOES[0],
  mva: "",
  // TXM-594: vazio = herda do nível acima (vai como null).
  aliquota: "",
  metodo: "",
};

/** .modal-grid do protótipo: grid-template-columns 1fr 1fr, gap 16px 18px. */
const GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: "16px 18px",
};
/** .span-2 => grid-column 1 / -1 (e volta a "auto" no breakpoint de 1 coluna). */
const SPAN_2 = { gridColumn: { xs: "auto", sm: "1 / -1" } };

/** .registro-secao-titulo */
const TITULO_SECAO = {
  fontSize: 13,
  fontWeight: 700,
  color: "#0d2847",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  margin: "4px 0 14px",
};

/** .modal-hint */
const HINT = { fontSize: 12, color: "#888", marginTop: "6px", display: "block" };

/**
 * Único ponto de entrada de dados novos (card seção 2.5). O Cliente fica no topo, fora dos
 * modos, porque vale para os três — e só pode ser um cliente já cadastrado: a tela não cria
 * cliente novo.
 *
 * Sem asterisco em label nenhum: o protótipo não marca obrigatoriedade visualmente em campo
 * algum, e o card é silencioso sobre isso (só fala de obrigatoriedade na EDIÇÃO, seção 2.4).
 * A validação continua existindo — ela só aparece como mensagem ao salvar, igual ao protótipo.
 */
const ModalNovoRegistro = ({
  open,
  onClose,
  grupos,
  podeEditarGlobal,
  onConcluido,
  setObjSnackBar,
}) => {
  const [modo, setModo] = useState(MODO.INDIVIDUAL);
  const [cliente, setCliente] = useState(null);
  const [cnpj, setCnpj] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [estadoLote, setEstadoLote] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [clienteOrigem, setClienteOrigem] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const inputArquivoRef = useRef(null);

  const { empresas, carregando: carregandoEmpresas, erro: erroEmpresas } = useEmpresasDoGrupo(cliente?.id);
  /** UF da empresa do CNPJ escolhido — quando existe, o Estado é ela e o campo fica travado. */
  const ufTravada = empresas.find((e) => e.cnpj === cnpj)?.uf || null;
  /**
   * Sem Cliente o item nasce global (vale para todos os clientes) — gravar alíquota/método
   * nele exige `ncm-table-edit-global` (decisão 09-30). Sem a permissão, os campos ficam
   * visíveis mas travados, com o motivo embaixo. A API valida de novo.
   */
  const aliquotaMetodoBloqueados = !cliente && !podeEditarGlobal;

  // Estado sempre acompanha a empresa: trocar Cliente ou CNPJ zera o Estado, e escolher um
  // CNPJ com UF cadastrada já preenche. Assim nunca sobra uma UF de uma escolha anterior.
  const aoTrocarCliente = (valor) => {
    setCliente(valor);
    setCnpj(null);
    // Limpar o Cliente sem a permissão global trava alíquota/método: zera o que foi digitado,
    // senão o campo travado mostraria um valor que não vai ser gravado.
    const vaiTravar = !valor && !podeEditarGlobal;
    setForm((f) => ({ ...f, estado: "", ...(vaiTravar && { aliquota: "", metodo: "" }) }));
    setEstadoLote("");
  };
  const aoTrocarCnpj = (empresa) => {
    setCnpj(empresa?.cnpj || null);
    const uf = empresa?.uf || "";
    setForm((f) => ({ ...f, estado: uf }));
    setEstadoLote(uf);
  };

  const fechar = () => {
    setModo(MODO.INDIVIDUAL);
    setCliente(null);
    setCnpj(null);
    setForm(FORM_VAZIO);
    setEstadoLote("");
    setArquivo(null);
    setClienteOrigem(null);
    if (inputArquivoRef.current) inputArquivoRef.current.value = "";
    onClose();
  };

  /** Devolve false para o fluxo de salvar poder abortar mantendo o modal aberto. */
  const erro = (msg) => {
    setObjSnackBar({ type: "error", msg });
    return false;
  };

  const lerArquivo = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Não foi possível ler o arquivo selecionado."));
      reader.readAsText(file, "UTF-8");
    });

  const salvarIndividual = async () => {
    if (!form.item.trim()) return erro("Informe o código do Item.");
    if (!form.ncm.trim() || !form.descricao.trim()) return erro("Informe NCM/SH e Descrição.");

    const resposta = await inserirItemTabelaNcm({
      // Cliente e Estado são opcionais — sem seleção, o item vale para "Todas as Empresas" /
      // "Todos os Estados" (decisão de produto 2026-09-21, usada pra carregar NCMs de
      // public.ncms, que não tem dono nem UF específicos).
      id_grupo_economico: cliente ? Number(cliente.id) : null,
      cnpj: cliente ? cnpj : null,
      estado: form.estado || null,
      item: form.item.trim(),
      // Só dígitos: "3002.42.00" vira "30024200" (a API faz o mesmo e exige 8 dígitos).
      ncm: sanitizarNcm(form.ncm),
      cest: form.cest.trim() || null,
      descricao: form.descricao.trim(),
      tipo: form.tipo,
      classificacao: form.classificacao,
      mva: form.mva.trim() || null,
      // aliq_interna / metodo_calculo (contrato TXM-594). Travados não vão: o item nasce com
      // os dois em null (herda), sem acionar a checagem de permissão global na API.
      ...(!aliquotaMetodoBloqueados && payloadAliquotaMetodo(form)),
    });
    setObjSnackBar({ type: "success", msg: resposta.message || "Item cadastrado." });
    return true;
  };

  const salvarPlanilha = async () => {
    if (!arquivo) return erro("Selecione um arquivo CSV para importar.");

    const { itens, erroArquivo } = parseCsvNcm(await lerArquivo(arquivo));
    if (erroArquivo) return erro(erroArquivo);

    const resposta = await importarLoteTabelaNcm({
      id_grupo_economico: cliente ? Number(cliente.id) : null,
      cnpj: cliente ? cnpj : null,
      estado: estadoLote || null,
      itens,
    });
    setObjSnackBar({ type: "success", msg: resposta.message || "Importação concluída." });
    return true;
  };

  const salvarCopia = async () => {
    if (!clienteOrigem) return erro("Selecione o cliente de origem.");
    if (String(clienteOrigem.id) === String(cliente.id)) {
      return erro("O cliente de origem deve ser diferente do cliente de destino.");
    }

    const resposta = await copiarBaseTabelaNcm({
      id_grupo_economico_origem: Number(clienteOrigem.id),
      id_grupo_economico_destino: Number(cliente.id),
      cnpj_destino: cnpj,
    });
    setObjSnackBar({ type: "success", msg: resposta.message || "Cópia concluída." });
    return true;
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    // Cliente só é obrigatório na Cópia de base (é o destino da cópia — sem ele não há pra
    // onde copiar). Nos outros dois modos, ausência de cliente significa "Todas as Empresas".
    if (modo === MODO.COPIA && !cliente) return erro("Selecione o cliente de destino.");
    // Com Cliente, o CNPJ é obrigatório nos três modos (na cópia, é o CNPJ de destino).
    if (cliente && !cnpj) {
      return erro(modo === MODO.COPIA ? "Selecione o CNPJ de destino." : "Selecione o CNPJ do cliente.");
    }

    setSalvando(true);
    try {
      let concluido;
      if (modo === MODO.INDIVIDUAL) concluido = await salvarIndividual();
      else if (modo === MODO.PLANILHA) concluido = await salvarPlanilha();
      else concluido = await salvarCopia();

      // Validação reprovada: mantém o modal aberto com o que já foi preenchido.
      if (!concluido) return;

      onConcluido();
      fechar();
    } catch (error) {
      erro(error.response?.data?.message || error.message || "Falha ao salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const campoTexto = (label, chave, placeholder, sx) => (
    <TextField
      label={label}
      placeholder={placeholder}
      value={form[chave]}
      onChange={(e) => setForm({ ...form, [chave]: e.target.value })}
      fullWidth
      size="small"
      sx={sx}
    />
  );

  const selecao = (label, valor, opcoes, aoMudar, sx) => (
    <Autocomplete
      size="small"
      options={opcoes}
      value={valor}
      onChange={(_, novo) => aoMudar(novo)}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={sx}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={fechar}
      fullWidth
      PaperProps={{ sx: { borderRadius: "10px", maxWidth: 640 } }}
    >
      {salvando ? (
        <Loading loading={salvando} />
      ) : (
        <form onSubmit={handleSalvar}>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: "#0d2847" }}>
            Novo Registro
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, py: 2.75 }}>
            <Box sx={{ ...GRID, mb: 2.25 }}>
              <Box sx={SPAN_2}>
                <Autocomplete
                  size="small"
                  options={grupos}
                  value={cliente}
                  onChange={(_, valor) => aoTrocarCliente(valor)}
                  getOptionLabel={(opcao) => opcao.detalhes || ""}
                  isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente" placeholder="Selecione um cliente…" />
                  )}
                />
                {/* Só nos modos individual/planilha: na Cópia de base, Cliente é o destino
                    da cópia e continua obrigatório. */}
                {modo !== MODO.COPIA && !cliente && (
                  <Typography component="span" sx={HINT}>
                    Deixe em branco para aplicar a "Todas as Empresas".
                  </Typography>
                )}
              </Box>
              {cliente && (
                <Box sx={SPAN_2}>
                  <CampoCnpj
                    empresas={empresas}
                    carregando={carregandoEmpresas}
                    erroCarga={erroEmpresas}
                    value={cnpj}
                    onChange={aoTrocarCnpj}
                    hint={
                      modo === MODO.COPIA
                        ? "CNPJ que vai receber os itens copiados."
                        : "Obrigatório quando há Cliente — o Estado passa a ser a UF desta empresa."
                    }
                  />
                </Box>
              )}
            </Box>

            {/* .modo-registro-opcoes — 3 cartões lado a lado, não uma lista de rádios */}
            <Box
              role="radiogroup"
              aria-label="Forma de inclusão dos dados"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1.25,
                mb: 2.5,
              }}
            >
              {MODOS.map((m) => {
                const selecionado = modo === m.valor;
                return (
                  <Box
                    component="label"
                    key={m.valor}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.5,
                      border: "1px solid",
                      borderColor: selecionado ? "#1e4a7a" : "#cfd8dc",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: 13,
                      lineHeight: 1.35,
                      bgcolor: selecionado ? "#eef4fb" : "transparent",
                      color: selecionado ? "#0d2847" : "#444",
                      fontWeight: selecionado ? 600 : 400,
                      "&:hover": { borderColor: "#1e4a7a", bgcolor: selecionado ? "#eef4fb" : "#f7f9fc" },
                    }}
                  >
                    <Radio
                      size="small"
                      checked={selecionado}
                      onChange={() => setModo(m.valor)}
                      value={m.valor}
                      sx={{ p: 0, mt: "2px" }}
                    />
                    <span>
                      {m.titulo}
                      <br />
                      <span style={{ fontWeight: 400, color: "#777" }}>{m.descricao}</span>
                    </span>
                  </Box>
                );
              })}
            </Box>

            {modo === MODO.INDIVIDUAL && (
              <Box>
                <Typography sx={TITULO_SECAO}>Novo item</Typography>
                <Box sx={GRID}>
                  <Box>
                    <Autocomplete
                      size="small"
                      options={UFS}
                      value={form.estado || null}
                      onChange={(_, v) => setForm({ ...form, estado: v || "" })}
                      disabled={Boolean(ufTravada)}
                      renderInput={(params) => (
                        <TextField {...params} label="Estado" placeholder="Todos os Estados" />
                      )}
                    />
                    <Typography component="span" sx={HINT}>
                      {ufTravada
                        ? "UF cadastrada para o CNPJ selecionado."
                        : 'Deixe em branco para aplicar a "Todos os Estados".'}
                    </Typography>
                  </Box>
                  {campoTexto("Item", "item", "Ex.: 1023")}
                  {campoTexto("NCM/SH", "ncm", "Ex.: 3002.42.00", SPAN_2)}
                  {campoTexto("CEST", "cest", "Ex.: 21.077.00")}
                  {campoTexto("Descrição", "descricao", "Descrição do item", SPAN_2)}
                  {selecao("Tipo", form.tipo, TIPOS_ITEM, (v) => setForm({ ...form, tipo: v }))}
                  {selecao("Classificação", form.classificacao, CLASSIFICACOES, (v) =>
                    setForm({ ...form, classificacao: v })
                  )}
                  <TextField
                    label="Valor MVA (%)"
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    placeholder="Ex.: 39,50"
                    value={form.mva}
                    onChange={(e) => setForm({ ...form, mva: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <CamposAliquotaMetodo
                    aliquota={form.aliquota}
                    metodo={form.metodo}
                    onChange={(parcial) => setForm({ ...form, ...parcial })}
                    bloqueado={aliquotaMetodoBloqueados}
                    motivoBloqueio={MOTIVO_SEM_PERMISSAO_GLOBAL}
                  />
                </Box>
              </Box>
            )}

            {modo === MODO.PLANILHA && (
              <Box>
                <Typography sx={TITULO_SECAO}>Upload de planilha</Typography>
                <Box sx={GRID}>
                  <Box>
                    <Autocomplete
                      size="small"
                      options={UFS}
                      value={estadoLote || null}
                      onChange={(_, v) => setEstadoLote(v || "")}
                      disabled={Boolean(ufTravada)}
                      renderInput={(params) => (
                        <TextField {...params} label="Estado" placeholder="Todos os Estados" />
                      )}
                    />
                    <Typography component="span" sx={HINT}>
                      {ufTravada
                        ? "UF cadastrada para o CNPJ selecionado."
                        : 'Deixe em branco para aplicar a "Todos os Estados".'}
                    </Typography>
                  </Box>
                  <Box sx={SPAN_2}>
                    <Typography
                      component="label"
                      htmlFor="planilhaArquivo"
                      sx={{ display: "block", fontSize: 13, fontWeight: 600, color: "#0d2847", mb: "6px" }}
                    >
                      Arquivo CSV
                    </Typography>
                    <Box
                      component="input"
                      id="planilhaArquivo"
                      ref={inputArquivoRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                      sx={{
                        width: "100%",
                        p: 1,
                        border: "1px solid #cfd8dc",
                        borderRadius: "6px",
                        fontSize: 14,
                        fontFamily: "inherit",
                        bgcolor: "#fafbfc",
                      }}
                    />
                    <Typography component="span" sx={HINT}>
                      Planilha em CSV (separada por ";" ou ","), com cabeçalho na primeira linha e
                      colunas Item, CEST, NCM/SH, Descrição, Tipo, Classificação e Valor MVA
                      (opcional) — a ordem das colunas é livre. Alíquota interna (%) e Método de
                      cálculo (PMC, MVA ou -) também são opcionais; em branco, herdam do nível
                      acima. O Estado selecionado ao lado (ou "Todos os Estados", se deixado em
                      branco) se aplica a todos os itens do arquivo.
                      {aliquotaMetodoBloqueados &&
                        " Sem Cliente, os itens valem para todas as empresas: preencher alíquota ou método exige a permissão de edição global da Tabela NCM."}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {modo === MODO.COPIA && (
              <Box>
                <Typography sx={TITULO_SECAO}>Cópia de base de outro cliente</Typography>
                <Box sx={GRID}>
                  <Box sx={SPAN_2}>
                    <Autocomplete
                      size="small"
                      options={grupos}
                      value={clienteOrigem}
                      onChange={(_, valor) => setClienteOrigem(valor)}
                      getOptionLabel={(opcao) => opcao.detalhes || ""}
                      isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                      renderInput={(params) => (
                        <TextField {...params} label="Cliente de origem" placeholder="Selecione…" />
                      )}
                    />
                    <Typography component="span" sx={HINT}>
                      Todos os itens do cliente de origem são copiados para o CNPJ selecionado no
                      topo, com a UF dele. Itens repetidos que caírem na mesma UF entram como
                      duplicados.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pt: 2, pb: 2.75 }}>
            <WhiteButton clickFunction={fechar} textValue="Cancelar" />
            <BlueButton textValue="Salvar" type="submit" />
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default ModalNovoRegistro;
